import { defineRule, configure } from 'vee-validate';
import { all } from '@vee-validate/rules';
import { i18n } from './i18n';

Object.entries(all).forEach(([name, rule]) => {
    defineRule(name, rule);
});

// Benutzerdefinierte Regeln

/** Validierung mit einem Regex */
defineRule('regex', (value, [pattern]) => {
    const reg = new RegExp(pattern);
    return reg.test(value) || i18n.global.t('allg.validate.invalidchars', [i18n.global.t(value)]);
});

/** Validiert eine Telefonnummer (Schweiz) */
defineRule('valid_swiss_telephone_number', (value) => {
    const reg = /^(((\+|00)\d{2})|0)\s?(\d{2})\s?(\d{3})\s?(\d{2})\s?(\d{2})\s?(\d{1,2})?$/;
    return reg.test(value) || i18n.global.t('allg.validate.phone', [i18n.global.t(value)]);
});

/** Validiert ein sicheres Passwort */
defineRule('valid_password_format', (value) => {
    const pattern =
        /^((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])|(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&+%*<>/=?_.,:;\\-])|(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%&+%*<>/=?_.,:;\\-])|(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%&+%*<>/=?_.,:;\\-])).{8,64}$/;
    return pattern.test(value) || i18n.global.t('allg.validate.passwordformat', [i18n.global.t(value)]);
});

/** Validiert das Protokoll einer URL */
defineRule('validUrlProtocol', (value) => {
    return /^(http|https):\/\//.test(value) || i18n.global.t('allg.validate.url', [i18n.global.t(value)]);
});

/** Validierung der Mindestlänge eines Wertes */
defineRule('min', (value, [min]) => {
    return value.length >= min || i18n.global.t('allg.validate.minlength', [i18n.global.t(value), min]);
});

/** Validierung der Maximallänge eines Wertes */
defineRule('max', (value, [max]) => {
    return value.length <= max || i18n.global.t('allg.validate.maxlength', [i18n.global.t(value), max]);
});

/** Validierung des minimal erlaubten Wertes */
defineRule('min_value', (value, [min]) => {
    return value >= min || i18n.global.t('allg.validate.minvalue', [i18n.global.t(value), min]);
});

/** Validierung des maximal erlaubten Wertes */
defineRule('max_value', (value, [max]) => {
    return value <= max || i18n.global.t('allg.validate.maxvalue', [i18n.global.t(value), max]);
});

/** Validiert eine Dezimalzahl */
defineRule('decimalNumber', (value) => {
    const isDouble = /^-?\d+(\.\d+)?$/.test(value);
    return isDouble || i18n.global.t('allg.validate.number', [i18n.global.t(value)]);
});

/** Validiert, ob zwei Werte übereinstimmen */
defineRule('confirmed', (value, [otherValue]) => {
    return value === otherValue || i18n.global.t('allg.validate.passwordnotequal', [i18n.global.t(value), otherValue]);
});

/** Validierung auf einen Maximalwert mit eigenem Label */
defineRule('max_value_labeled', (value, { max, label }) => {
    return value <= max || i18n.global.t(label, [i18n.global.t(value), max]);
});

/** Validierung auf einen Minimalwert mit eigenem Label */
defineRule('min_value_labeled', (value, { min, label }) => {
    return value >= min || i18n.global.t(label, [i18n.global.t(value), min]);
});

/** Validiert, ob der Wert mit einem bestimmten Präfix beginnt */
defineRule('starts_with', (value: string, [prefix]: [string]) => {
    if (!value) {
        return true;
    } // let `required` handle empty values
    return value.startsWith(prefix) || i18n.global.t('allg.validate.startwith') + ' ' + prefix;
});

configure({
    generateMessage: ({ field, rule }) => {
        const messages = {
            // Default error messages for built-in rules
            is: 'allg.validate.is',
            regex: `allg.validate.invalidchars`,
            required: `allg.validate.required`,
            valid_swiss_telephone_number: 'allg.validate.phone',
            valid_password_format: 'passwordformat',
            validUrlProtocol: 'allg.validate.url',
            min: 'allg.validate.minlength',
            max: 'allg.validate.maxlength',
            min_value: 'allg.validate.minvalue',
            max_value: 'allg.validate.maxvalue',
            numeric: 'allg.validate.number',
            double: 'allg.validate.number',
            decimalNumber: 'allg.validate.number',
            email: 'allg.validate.email',
            confirmed: 'allg.validate.passwordnotequal',
            between: 'allg.validate.between'
        };

        if (rule.name in messages) {
            const values = Object.values(rule.params);
            const args = [i18n.global.t(field).toString()];

            //add all args for placeholder replacements
            values.forEach((val) => {
                args.push(val as string);
            });

            return i18n.global.t(messages[rule.name], args) as string;
        }

        // Fallback
        return i18n.global.t('allg.validate.invalid');
    }
});
